import { IconCheck, IconCopy } from '@tabler/icons-react';
import { copyToClipboard } from '@xver/utils/clipboard';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DomainRecordsSectionsProps {
    domainName: string;
    txtRecordName: string;
    txtRecord: string;
    dnsTarget?: string;
    dnsRecordType?: string;
}

function RecordRow({
    label,
    value,
    copyable = true,
}: {
    label: string;
    value: string;
    copyable?: boolean;
}) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="w-full flex items-center gap-3">
            <span className="text-xs text-fg/68 w-12 shrink-0">{label}</span>
            <code className="flex-1 font-mono text-xs text-fg/80 px-2 py-1.5 rounded bg-surface-2 border-[0.5px] border-line truncate">
                {value}
            </code>
            {copyable && (
                <button
                    type="button"
                    aria-label={`Copy ${label}`}
                    title={`Copy ${label}`}
                    onClick={async () => {
                        try {
                            await copyToClipboard(value);
                            setCopied(true);
                            window.setTimeout(() => setCopied(false), 1500);
                        } catch {
                            // ignore
                        }
                    }}
                    className="inline-flex shrink-0 items-center justify-center rounded-md border border-line bg-surface-2 px-1.5 py-1.5 text-fg/68 transition-colors hover:bg-surface-3 hover:text-fg"
                >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                </button>
            )}
        </div>
    );
}

export default function DomainRecordsSections({
    domainName,
    txtRecordName,
    txtRecord,
    dnsTarget,
    dnsRecordType,
}: DomainRecordsSectionsProps) {
    const { t } = useTranslation('containerEngineInstance');

    return (
        <>
            <div className="w-full flex flex-col gap-2.5">
                <span className="text-xs font-semibold text-fg/68">
                    {t('domains.verifyRecordTitle', '1) Verify ownership — TXT record')}
                </span>
                <RecordRow
                    label={t('domains.recordTypeLabel', 'Type')}
                    value="TXT"
                    copyable={false}
                />
                <RecordRow label={t('domains.recordNameLabel', 'Name')} value={txtRecordName} />
                <RecordRow label={t('domains.recordValueLabel', 'Value')} value={txtRecord} />
            </div>
            {dnsTarget && (
                <div className="w-full flex flex-col gap-2.5">
                    <span className="text-xs font-semibold text-fg/68">
                        {t('domains.routeRecordTitle', '2) Route traffic — point your domain')}
                    </span>
                    <RecordRow
                        label={t('domains.recordTypeLabel', 'Type')}
                        value={dnsRecordType || 'CNAME'}
                        copyable={false}
                    />
                    <RecordRow label={t('domains.recordNameLabel', 'Name')} value={domainName} />
                    <RecordRow label={t('domains.recordValueLabel', 'Value')} value={dnsTarget} />
                </div>
            )}
        </>
    );
}
